import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { VideoCard, VideoLightbox, SectionTitle } from "../components/VideoCard";
import { Reveal } from "../hooks/useReveal";
import { scrambleTo } from "../lib/scramble";

/* ---------- Hero: fast-cut montage background ----------
   All clips stay mounted (local files, muted, looping) and we hard-cut
   between them. Cut speeds are editable from the admin panel:
   hero.cutSeconds per clip, hero.lastCutSeconds for the final clip. */
function HeroTitle({ title }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !title) return;
    const words = [title.toUpperCase(), "AI VIDEO", "STORYTELLING", "STUDIO"];
    el.textContent = words[0];
    let wi = 0;
    let interval;
    const delay = document.querySelector(".preloader") ? 2200 : 150;
    const t = setTimeout(() => {
      scrambleTo(el, words[0]);
      interval = setInterval(() => {
        wi = (wi + 1) % words.length;
        scrambleTo(el, words[wi], { charTime: 38, perCharDelay: 4 });
      }, 3000);
    }, delay);
    return () => {
      clearTimeout(t);
      clearInterval(interval);
      if (el.__anim) cancelAnimationFrame(el.__anim);
    };
  }, [title]);

  return (
    <h1
      ref={ref}
      className="relative font-comfortaa font-light text-white leading-none tracking-tight scramble scramble-big"
      style={{
        fontSize: "clamp(44px, 9.5vw, 148px)",
        letterSpacing: "-0.02em",
        textShadow: "0 2px 30px rgba(0,0,0,0.85), 0 1px 6px rgba(0,0,0,0.6)",
      }}
      data-testid="hero-title"
    />
  );
}


function HeroVideo({ hero }) {
  const clips = hero.videos && hero.videos.length > 0 ? hero.videos : [""];
  const [idx, setIdx] = useState(0);

  const cutMs = Math.max(0.5, Number(hero.cutSeconds) || 2) * 1000;
  const lastCutMs = Math.max(0.5, Number(hero.lastCutSeconds) || 3) * 1000;

  useEffect(() => {
    if (clips.length <= 1) return;
    // last clip in the playlist holds a beat longer, the rest cut faster
    const hold = idx === clips.length - 1 ? lastCutMs : cutMs;
    const t = setTimeout(() => setIdx((i) => (i + 1) % clips.length), hold);
    return () => clearTimeout(t);
  }, [idx, clips.length, cutMs, lastCutMs]);

  return (
    <section className="relative h-screen w-full overflow-hidden bg-[#141414]" data-testid="hero-section">
      {clips.map((src, i) => (
        <video
          key={`${src}-${i}`}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: i === idx ? 1 : 0 }}
          data-testid={i === idx ? "hero-video" : `hero-video-hidden-${i}`}
          data-active={i === idx ? "true" : "false"}
        />
      ))}
      <div className="absolute inset-0 bg-black/35" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        {/* Soft dark backdrop so the title stays readable over any video */}
        <div className="relative px-6 py-10 md:px-16 md:py-14">
          <div
            className="absolute inset-0 -inset-x-10 md:-inset-x-24"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 45%, rgba(0,0,0,0) 75%)",
            }}
          />
          <HeroTitle title={hero.title} />
          <h3
            className="relative font-comfortaa italic text-white mt-4 md:mt-6"
            style={{
              fontSize: "clamp(17px, 2.2vw, 30px)",
              textShadow: "0 2px 20px rgba(0,0,0,0.85), 0 1px 5px rgba(0,0,0,0.6)",
            }}
            data-testid="hero-subtitle"
          >
            <em>{hero.subtitle}</em>
          </h3>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce-slow">
        <ChevronDown size={30} className="text-white/70" strokeWidth={1.2} />
      </div>
    </section>
  );
}

export default function Home() {
  const { content, videos } = useContent();
  const [playing, setPlaying] = useState(null);
  const featured = videos.filter((v) => v.featured).sort((a, b) => a.order - b.order);

  return (
    <div className="bg-[#141414] texture-dark">
      <HeroVideo hero={content.hero} />

      {/* Intro — light gray, diagonal bottom */}
      <section className="bg-[#d8d8d8] slant-bottom-right relative z-10" data-testid="intro-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-28 md:pb-36">
          <Reveal>
            <h3 className="font-comfortaa text-neutral-900 text-center leading-relaxed font-normal"
              style={{ fontSize: "clamp(18px, 2.1vw, 27px)" }}>
              {content.home.intro}
            </h3>
          </Reveal>
        </div>
      </section>

      {/* Sample Work — dark textured */}
      <section className="texture-dark pt-20 md:pt-24 pb-24" data-testid="sample-work-section">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionTitle>{content.home.sampleWorkTitle}</SectionTitle>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12">
            {featured.map((v, i) => (
              <Reveal key={v.id} delay={i * 90}>
                <VideoCard video={v} onPlay={setPlaying} />
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-16">
            <Link to="/portfolio" className="pill-button inline-block" data-testid="view-portfolio-button">
              {content.home.portfolioButton}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Team statement — light gray, diagonal top + bottom */}
      <section className="bg-[#d8d8d8] slant-top-left slant-bottom-left relative z-10" data-testid="statement-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-28 md:pb-36">
          <Reveal>
            <h3 className="font-comfortaa text-neutral-900 text-center leading-relaxed font-normal"
              style={{ fontSize: "clamp(18px, 2.1vw, 27px)" }}>
              {content.home.teamStatement}
            </h3>
          </Reveal>
        </div>
      </section>

      {/* AI tools marquee — premium strip */}
      <section className="texture-dark py-10 md:py-12 overflow-hidden border-y border-white/10" data-testid="tools-marquee">
        <div className="marquee-track">
          {[...(content.about.tools || []), ...(content.about.tools || [])].map((t, i) => (
            <span key={i} className="flex items-center shrink-0">
              <span className="font-comfortaa text-white/45 text-sm md:text-base whitespace-nowrap px-6">
                {t}
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#8fae4f]/60 shrink-0" />
            </span>
          ))}
        </div>
      </section>

      {/* Trust section — hidden until enabled from the admin panel */}
      {content.trust?.enabled && (
        <section className="texture-dark pt-20 md:pt-24 pb-10" data-testid="trust-section">
          <div className="max-w-[1240px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="kicker text-white/40 mb-4">{content.trust.kicker}</p>
              <SectionTitle>{content.trust.title}</SectionTitle>
            </Reveal>
            {content.trust.brands?.length > 0 && (
              <Reveal>
                <div className="flex flex-wrap items-center gap-x-12 gap-y-5 mb-16">
                  {content.trust.brands.map((b) => (
                    <span
                      key={b}
                      className="font-comfortaa text-white/40 text-lg md:text-xl tracking-wide hover:text-white/70"
                      style={{ transition: "color 0.3s ease" }}
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </Reveal>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {(content.trust.testimonials || [])
                .filter((t) => t.quote)
                .map((t, i) => (
                  <Reveal key={i} delay={i * 90}>
                    <div className="border-t border-white/25 pt-5 h-full">
                      <p className="font-comfortaa text-white/85 text-sm leading-relaxed">
                        &ldquo;{t.quote}&rdquo;
                      </p>
                      <p className="font-comfortaa text-white text-xs mt-5">{t.author}</p>
                      <p className="font-comfortaa text-white/50 text-[11px] mt-0.5">{t.role}</p>
                    </div>
                  </Reveal>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* Collage image */}
      <section className="texture-dark py-16 md:py-20" data-testid="collage-section">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12">
          <Reveal>
            <img
              src={content.home.collageImage}
              alt="Behind the scenes at DavidDoesAI Studio"
              loading="lazy"
              className="w-full rounded-lg object-cover max-h-[640px]"
            />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="texture-dark pb-28 pt-8 md:pt-12" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="kicker text-white/40 mb-6">Ready when you are</p>
            <h1 className="font-comfortaa font-light text-white"
              style={{ fontSize: "clamp(34px, 5.5vw, 72px)", letterSpacing: "-0.01em" }}>
              {content.home.ctaTitle}
            </h1>
          </Reveal>
          <Reveal delay={150}>
            <Link to="/contact" className="pill-button inline-block mt-10" data-testid="home-contact-button">
              {content.home.ctaButton}
            </Link>
          </Reveal>
        </div>
      </section>

      {playing && <VideoLightbox video={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}
